import { pb, fileUrl } from "@/lib/pocketbase";
import { downscaleImage } from "@/lib/image";
import { pbRepository } from "./pocketbase-repository";
import { auth } from "./auth.service";
import type { Homework } from "@/types/models";

const repo = pbRepository<Homework>("homework");

function normalize(hw: Homework): Homework {
  const photos = (hw as { photos?: unknown }).photos;
  return {
    ...hw,
    dueDate: (hw.dueDate ?? "").slice(0, 10),
    done: Boolean(hw.done),
    duePeriod: hw.duePeriod || undefined,
    photos: Array.isArray(photos) ? (photos as string[]) : photos ? [String(photos)] : [],
  };
}

export interface HomeworkInput {
  subject: string;
  topic?: string;
  text: string;
  dueDate: string;
  duePeriod?: number;
  /** Notiz, aus der die Aufgabe stammt (siehe features/homework/fromNotes.ts). */
  sourceNote?: string;
}

export const homeworkService = {
  listAll(): Promise<Homework[]> {
    return repo.list({ sort: "dueDate", perPage: 500 }).then((l) => l.map(normalize));
  },

  listByTopic(topicId: string): Promise<Homework[]> {
    return repo
      .list({ filter: pb.filter("topic = {:t}", { t: topicId }), sort: "dueDate" })
      .then((l) => l.map(normalize));
  },

  /** Hausaufgaben zum Thema + ältere Einträge desselben Fachs ohne Thema-Zuordnung. */
  listByTopicWithFallback(topicId: string, subjectId: string): Promise<Homework[]> {
    return repo
      .list({
        filter: pb.filter("topic = {:t} || (subject = {:s} && topic = '')", { t: topicId, s: subjectId }),
        sort: "dueDate",
      })
      .then((l) => l.map(normalize));
  },

  create(data: HomeworkInput): Promise<Homework> {
    return repo
      .create({
        ...data,
        duePeriod: data.duePeriod ?? null,
        done: false,
        owner: auth.currentUser()?.id,
      } as unknown as Partial<Homework>)
      .then(normalize);
  },

  update(id: string, data: Partial<Homework>): Promise<Homework> {
    return repo.update(id, data).then(normalize);
  },

  remove(id: string): Promise<void> {
    return repo.remove(id);
  },

  /** Haengt ein Foto an, ohne bestehende zu ersetzen (`photos+`). */
  async addPhoto(id: string, file: File): Promise<Homework> {
    const fd = new FormData();
    fd.append("photos+", await downscaleImage(file));
    return repo.update(id, fd).then(normalize);
  },

  urlFor(hw: Homework, filename: string): string {
    return fileUrl(hw as unknown as Record<string, unknown>, filename);
  },
};
